import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Home & Auto", "Life & Health", "Medicaid"];
const STATUSES = ["New", "Contacted", "Quoted", "Closed"];

const STATUS_STYLES = {
  New: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  Contacted: "border-blue-400/30 bg-blue-400/10 text-blue-300",
  Quoted: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  Closed: "border-slate-400/30 bg-slate-400/10 text-slate-300",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quotes`, { withCredentials: true });
      setQuotes(data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
      else toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
        load();
      } catch {
        navigate("/admin/login");
      }
    })();
  }, [navigate, load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, { withCredentials: true });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/admin/login");
  };

  const filtered = filter === "All" ? quotes : quotes.filter((q) => q.insurance_type === filter);
  const newCount = quotes.filter((q) => q.status === "New").length;

  return (
    <div className="min-h-screen bg-[#0B0F17] font-sans text-slate-100">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <Logo size={34} testId="admin-logo" />
            <div className="leading-none">
              <p className="font-serif text-lg">Quote Enquiries</p>
              <p data-testid="admin-user-label" className="mt-1 text-xs text-slate-400">
                {user ? `${user.name} · ${user.email}` : "…"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setLoading(true); load(); }}
              data-testid="admin-refresh-btn"
              className="rounded-full border border-white/10 p-2.5 text-slate-400 transition-colors hover:text-[#E2A85C]"
              aria-label="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2.5 text-sm text-slate-300 transition-colors hover:border-red-400/40 hover:text-red-300"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <div data-testid="stat-total" className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
            <p className="font-serif text-3xl text-[#E2A85C]">{quotes.length}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">Total</p>
          </div>
          <div data-testid="stat-new" className="rounded-2xl border border-amber-400/20 bg-amber-400/[0.06] p-5">
            <p className="font-serif text-3xl text-amber-300">{newCount}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">New</p>
          </div>
          {TYPES.map((t) => (
            <div key={t} data-testid={`stat-type-${t.replace(/[^a-z]/gi, "-").toLowerCase()}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
              <p className="font-serif text-3xl text-slate-100">
                {quotes.filter((q) => q.insurance_type === t).length}
              </p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">{t}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-filters">
          {["All", ...TYPES].map((t) => (
            <button
              key={t}
              data-testid={`filter-${t.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              onClick={() => setFilter(t)}
              className={`rounded-full border px-4 py-2 text-sm transition-all duration-300 ${
                filter === t
                  ? "border-[#E2A85C] bg-[#E2A85C]/15 text-[#E2A85C]"
                  : "border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10" data-testid="quotes-table-wrap">
          {loading ? (
            <p className="p-10 text-center text-slate-400" data-testid="quotes-loading">Loading enquiries…</p>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-14 text-center" data-testid="quotes-empty">
              <Inbox size={32} className="text-slate-500" />
              <p className="text-slate-400">No enquiries here yet. New quote requests will appear in this list.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-left text-sm" data-testid="quotes-table">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.03] text-xs uppercase tracking-[0.15em] text-slate-400">
                    <th className="px-5 py-4 font-semibold">Received</th>
                    <th className="px-5 py-4 font-semibold">Name</th>
                    <th className="px-5 py-4 font-semibold">Contact</th>
                    <th className="px-5 py-4 font-semibold">Coverage</th>
                    <th className="px-5 py-4 font-semibold">Message</th>
                    <th className="px-5 py-4 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => (
                    <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                      <td className="whitespace-nowrap px-5 py-4 font-mono text-xs text-slate-400">
                        {new Date(q.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </td>
                      <td className="px-5 py-4 font-medium text-slate-100">{q.name}</td>
                      <td className="px-5 py-4 text-slate-300">
                        <a href={`mailto:${q.email}`} className="block transition-colors hover:text-[#E2A85C]">{q.email}</a>
                        <a href={`tel:${q.phone}`} className="block font-mono text-xs text-slate-400 transition-colors hover:text-[#E2A85C]">{q.phone}</a>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-300">{q.insurance_type}</td>
                      <td className="max-w-[220px] truncate px-5 py-4 text-slate-400" title={q.message}>
                        {q.message || "—"}
                      </td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`status-select-${q.id}`}
                          value={q.status}
                          onChange={(e) => updateStatus(q.id, e.target.value)}
                          className={`cursor-pointer appearance-none rounded-full border bg-transparent px-3 py-1.5 text-xs font-semibold ${STATUS_STYLES[q.status] || STATUS_STYLES.New}`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s} className="bg-[#111827] text-slate-100">
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
